/**
 * Moteur de composition narrative — assemble une phrase d'impact à partir
 * des `consequences` machine-lisibles d'une option de décision, en piochant
 * dans une bibliothèque de tropes pré-rédigés.
 *
 * Pipeline :
 *  1. Pour chaque conséquence de l'option, scanner les tropes qui matchent
 *     (target compatible + signe du delta + magnitude au-dessus du seuil).
 *  2. Filtrer ceux dont `indicator_link` n'est pas dans `revealedIndicators`
 *     (sauf si pas d'`indicator_link` — le trope est alors générique).
 *  3. Garder les 2 tropes au poids le plus élevé.
 *  4. Si aucun trope ne matche, retomber sur le fallback positif/négatif/neutre
 *     selon le delta dominant de l'option.
 *  5. Composer une phrase : capitale initiale, virgule entre tropes, point final.
 *
 * Si l'option a un `impact_summary` rédigé à la main, on l'utilise tel quel.
 */

import type {
    Consequence,
    DecisionOption,
    ImpactCurve,
    TargetSelector,
} from "./types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TropeMatch {
    target_kind:
        | "by_csp"
        | "by_age"
        | "by_health"
        | "by_politics"
        | "economy"
        | "all"
        | "any";
    target_keys?: string[];
    delta_sign: "+" | "-" | "any";
    min_magnitude: number;
    /** Gating par domaine de décision (2026-07-16) — les tropes très
     *  spécifiques (Tsarat, empreinte carbone, PISA…) ne matchent que si
     *  la décision appartient au bon domaine, sinon ils racontent
     *  n'importe quoi (« le Tsarat réagit » à une réforme des retraites). */
    domains?: string[];
}

export interface Trope {
    id: string;
    phrase: string;
    match: TropeMatch;
    indicator_link?: string;
    category: string;
    weight?: number;
}

export interface TropesLibrary {
    version: string;
    doc?: string;
    tropes: Trope[];
}

// ---------------------------------------------------------------------------
// Loader
// ---------------------------------------------------------------------------

let cachedLibrary: TropesLibrary | null = null;

export async function loadTropes(
    url = "/data/narratives/impact_tropes.json",
): Promise<TropesLibrary> {
    if (cachedLibrary) return cachedLibrary;
    const response = await fetch(url);
    cachedLibrary = (await response.json()) as TropesLibrary;
    return cachedLibrary;
}

export function setLibraryForTesting(lib: TropesLibrary): void {
    cachedLibrary = lib;
}

// ---------------------------------------------------------------------------
// Utilitaires
// ---------------------------------------------------------------------------

function curveDelta(curve: ImpactCurve): number {
    if (curve.type === "linear" || curve.type === "additive") return curve.delta;
    return 0;
}

function targetKeys(target: TargetSelector): { kind: string; keys: string[] } {
    switch (target.type) {
        case "by_age":
            return { kind: "by_age", keys: target.ages };
        case "by_csp":
            return { kind: "by_csp", keys: target.csps };
        case "by_health":
            return { kind: "by_health", keys: target.healths };
        case "by_politics":
            return { kind: "by_politics", keys: target.politics };
        case "all":
            return { kind: "all", keys: [] };
        case "intersection":
            // On aplatit les clés de toutes les sous-cibles. Un trope qui
            // matche n'importe laquelle est candidat — on assume que
            // l'intersection touche toutes ces cohortes ensemble.
            return target.selectors
                .map((s) => targetKeys(s))
                .reduce(
                    (acc, x) => ({
                        kind: x.kind === "all" ? acc.kind : x.kind,
                        keys: [...acc.keys, ...x.keys],
                    }),
                    { kind: "any", keys: [] as string[] },
                );
        case "economy":
            return { kind: "economy", keys: [target.component] };
    }
}

function tropeMatches(
    trope: Trope,
    consequence: Consequence,
): { matched: boolean; magnitude: number; sign: "+" | "-" | "0" } {
    const { kind, keys } = targetKeys(consequence.target);

    // 1. target_kind doit matcher (ou être "any" / "all" qui acceptent tout)
    if (
        trope.match.target_kind !== "any" &&
        trope.match.target_kind !== kind &&
        !(trope.match.target_kind === "all" && kind === "all")
    ) {
        return { matched: false, magnitude: 0, sign: "0" };
    }

    // 2. target_keys : au moins une clé du trope doit être présente dans
    //    les clés de la cible. Si le trope n'a pas de target_keys, c'est
    //    permissif (matche n'importe quelle cible du bon kind).
    if (trope.match.target_keys && trope.match.target_keys.length > 0) {
        const hasOverlap = trope.match.target_keys.some((k) =>
            keys.includes(k),
        );
        if (!hasOverlap) {
            return { matched: false, magnitude: 0, sign: "0" };
        }
    }

    // 3. magnitude + signe : on prend le delta max en valeur absolue parmi
    //    les impacts de la conséquence. Le signe est celui de ce delta.
    let bestMag = 0;
    let bestSign: "+" | "-" | "0" = "0";
    for (const impact of consequence.impacts) {
        const d = curveDelta(impact.curve);
        // additif : magnitude = |d| ; linéaire (ratio) : on multiplie par 100
        // pour comparer aux mêmes seuils que les `additive` couramment
        // utilisés (qui sont en points de satisfaction).
        const mag =
            impact.curve.type === "linear" ? Math.abs(d * 100) : Math.abs(d);
        if (mag > bestMag) {
            bestMag = mag;
            bestSign = d > 0 ? "+" : d < 0 ? "-" : "0";
        }
    }

    if (bestMag < trope.match.min_magnitude) {
        return { matched: false, magnitude: 0, sign: "0" };
    }
    if (
        trope.match.delta_sign !== "any" &&
        trope.match.delta_sign !== bestSign
    ) {
        return { matched: false, magnitude: 0, sign: "0" };
    }

    return { matched: true, magnitude: bestMag, sign: bestSign };
}

function dominantSign(option: DecisionOption): "+" | "-" | "0" {
    let sumAbs = 0;
    let sumSigned = 0;
    for (const c of option.consequences ?? []) {
        for (const i of c.impacts) {
            const d = curveDelta(i.curve);
            const mag = i.curve.type === "linear" ? Math.abs(d * 100) : Math.abs(d);
            sumAbs += mag;
            sumSigned += d * (i.curve.type === "linear" ? 100 : 1);
        }
    }
    if (sumAbs < 0.5) return "0";
    return sumSigned > 0 ? "+" : sumSigned < 0 ? "-" : "0";
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

/**
 * Compose la phrase d'impact pour une option.
 *
 * @param option              l'option à résumer
 * @param library             la bibliothèque de tropes (chargée via `loadTropes`)
 * @param revealedIndicators  ensemble des indicateurs déjà révélés au joueur
 *                            (les tropes liés à un indicateur non révélé sont écartés)
 */
export function summarizeOption(
    option: DecisionOption,
    library: TropesLibrary,
    revealedIndicators: ReadonlySet<string>,
    /** Domaine de la décision parente — active le gating `match.domains`. */
    decisionDomain?: string,
): string {
    // Override manuel : priorité absolue.
    if (option.impact_summary && option.impact_summary.trim().length > 0) {
        return option.impact_summary;
    }

    // 1. Collecter les candidats (trope, magnitude) en respectant la révélation.
    type Candidate = { trope: Trope; magnitude: number };
    const candidates: Candidate[] = [];

    for (const consequence of option.consequences ?? []) {
        for (const trope of library.tropes) {
            if (trope.category === "fallback") continue;
            if (
                trope.indicator_link &&
                !revealedIndicators.has(trope.indicator_link)
            ) {
                continue;
            }
            if (
                trope.match.domains &&
                (!decisionDomain || !trope.match.domains.includes(decisionDomain))
            ) {
                continue;
            }
            const m = tropeMatches(trope, consequence);
            if (m.matched) {
                candidates.push({ trope, magnitude: m.magnitude });
            }
        }
    }

    // 2. Dédupliquer par id + trier par poids puis par magnitude.
    const byId = new Map<string, Candidate>();
    for (const c of candidates) {
        const existing = byId.get(c.trope.id);
        if (!existing || c.magnitude > existing.magnitude) {
            byId.set(c.trope.id, c);
        }
    }
    // Anti-répétition (2026-07-16) : à poids égal, l'ordre est ROTATIF par
    // option (hash stable de l'id) au lieu d'être toujours identique — deux
    // options aux conséquences semblables ne montrent plus la même phrase,
    // et une même option reste déterministe (replay, re-render).
    const seed = hashString(option.id);
    const sorted = [...byId.values()].sort((a, b) => {
        const wa = a.trope.weight ?? 1;
        const wb = b.trope.weight ?? 1;
        if (wb !== wa) return wb - wa;
        const ra = hashString(option.id + a.trope.id);
        const rb = hashString(option.id + b.trope.id);
        if (ra !== rb) return ra - rb;
        return b.magnitude - a.magnitude;
    });

    // 3. Garder 2 tropes max. On évite deux tropes de la même catégorie sauf
    //    si l'un est un avertissement (qui s'ajoute toujours à un autre ton).
    const picked: Trope[] = [];
    const seenCats = new Set<string>();
    for (const c of sorted) {
        if (picked.length >= 2) break;
        const cat = c.trope.category;
        if (seenCats.has(cat) && cat !== "warning") continue;
        picked.push(c.trope);
        seenCats.add(cat);
    }

    // 4. Fallback FISCAL (2026-07-16) : 26 % des options n'ont d'effet
    //    que budgétaire (audit : 201/762 tombaient sur le fallback neutre).
    //    Si l'option porte un impact fiscal net, on le DIT — c'est la
    //    donnée la plus instructive qu'on ait.
    if (picked.length === 0 && option.fiscal_impact) {
        const fi = option.fiscal_impact as {
            delta_revenue_billions?: number;
            delta_expense_billions?: number;
        };
        const rev = fi.delta_revenue_billions ?? 0;
        const exp = fi.delta_expense_billions ?? 0;
        const parts: string[] = [];
        const fmt = (n: number) =>
            Math.abs(n) >= 10 ? Math.round(Math.abs(n)).toString() : Math.abs(n).toFixed(1).replace(/\.0$/, "");
        if (exp > 0.05) parts.push(`≈${fmt(exp)} Md€ de dépense nouvelle — le budget encaisse`);
        else if (exp < -0.05) parts.push(`≈${fmt(exp)} Md€ d'économies — quelqu'un les sentira passer`);
        if (rev > 0.05) parts.push(`≈${fmt(rev)} Md€ de recettes en plus dans les caisses`);
        else if (rev < -0.05) parts.push(`un manque à gagner d'≈${fmt(rev)} Md€ pour les caisses`);
        if (parts.length > 0) {
            return composeSentence([
                "l'essentiel se jouera dans les colonnes du budget : " + parts.join(", "),
            ]);
        }
    }

    // 4b. Fallback si rien — variantes rotatives (le fallback était LA
    //    phrase la plus vue du jeu, cf. audit 2026-07-16).
    if (picked.length === 0) {
        const sign = dominantSign(option);
        const base =
            sign === "+"
                ? "fallback_unrevealed_positive"
                : sign === "-"
                  ? "fallback_unrevealed_negative"
                  : "fallback_neutral";
        const variants = library.tropes.filter(
            (t) => t.id === base || t.id.startsWith(base + "_"),
        );
        if (variants.length > 0) {
            picked.push(variants[seed % variants.length]);
        }
    }

    // 5. Composer.
    return composeSentence(picked.map((t) => t.phrase));
}

/** Hash déterministe (djb2 simplifié) — stable entre sessions/replays. */
function hashString(s: string): number {
    let h = 5381;
    for (let i = 0; i < s.length; i++) {
        h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    }
    return Math.abs(h);
}

function composeSentence(fragments: string[]): string {
    if (fragments.length === 0) {
        return "L'effet sera discret.";
    }
    const joined =
        fragments.length === 1
            ? fragments[0]
            : fragments.slice(0, -1).join(" ; ") + " — " + fragments[fragments.length - 1];
    const capitalized = joined.charAt(0).toUpperCase() + joined.slice(1);
    return capitalized.endsWith(".") ? capitalized : capitalized + ".";
}
